import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { refreshClinic } from "../lib/clinic";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = React.useRef(false);
  // MT-11C.0: several clinics (or clinic + JMDC) → the server never picks one silently
  const [choices, setChoices] = useState(null);
  const [busy, setBusy] = useState("");

  const finish = async () => {
    // MT-11B.7: the session response is identity-only; the authoritative
    // profile (role, prescriber linkage from the tenant membership,
    // entitlements) comes from /auth/me — load it BEFORE any page renders.
    const me = await api.get("/auth/me");
    setUser(me.data);
    // MT-11C.2: the pre-login /settings/clinic read was anonymous — replace the
    // branding with the ACTIVE tenant's profile before any page renders.
    await refreshClinic();
    window.history.replaceState({}, "", "/dashboard");
    navigate("/dashboard", { replace: true, state: { user: me.data } });
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const sid = m[1];
    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id: sid });
        if (r.data?.status === "TENANT_SELECTION_REQUIRED") {
          setChoices(r.data.choices || []);
          return;
        }
        await finish();
      } catch (e) {
        // PHASE-B1: uninvited accounts get a neutral denial on the login page;
        // MT-11B.7: anything else shows a visible generic sign-in error.
        navigate(e?.response?.status === 403 ? "/?denied=1" : "/?error=1", { replace: true });
      }
    })();
  }, [location, navigate, setUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (tenantId) => {
    setBusy(tenantId);
    try {
      await api.post("/auth/select-tenant", { tenant_id: tenantId });
      await finish();
    } catch (e) {
      navigate(e?.response?.status === 403 ? "/?denied=1" : "/?error=1", { replace: true });
    }
  };

  if (choices) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-6 space-y-3" data-testid="tenant-selection">
          <h1 className="text-lg font-semibold text-slate-900">Choose your clinic</h1>
          <p className="text-sm text-slate-600">Your account belongs to more than one clinic. Pick the one to sign in to.</p>
          {choices.map((c) => (
            <button key={c.tenant_id} type="button" disabled={!!busy} onClick={() => choose(c.tenant_id)}
              className="w-full text-left px-4 py-3 rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`tenant-choice-${c.slug || c.tenant_id}`}>
              <div className="font-medium text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500">{c.role}{busy === c.tenant_id ? " · signing in…" : ""}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-600" data-testid="auth-callback-loading">Signing you in…</div>
    </div>
  );
}
